import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabasePublishableKey = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

const hasSupabaseConfig = Boolean(supabaseUrl && supabasePublishableKey);

const missingConfigErrorMessage =
	"Missing Supabase configuration. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY before building.";

function createMissingConfigClient(): SupabaseClient {
	return new Proxy({} as SupabaseClient, {
		get() {
			throw new Error(missingConfigErrorMessage);
		},
	});
}

/**
 * Simple promise-chaining mutex for Supabase auth locks.
 * Replaces the default Web Locks API implementation to prevent
 * "lock was released because another request stole it" warnings that
 * occur under React StrictMode / Turbopack concurrent auth calls.
 */
const _locks = new Map<string, Promise<unknown>>();
function authLock<R>(
	name: string,
	_acquireTimeout: number,
	fn: () => Promise<R>,
): Promise<R> {
	const tail = (_locks.get(name) ?? Promise.resolve()).then(fn).finally(() => {
		if (_locks.get(name) === tail) _locks.delete(name);
	}) as Promise<R>;
	_locks.set(name, tail);
	return tail;
}

export const supabase: SupabaseClient = hasSupabaseConfig
	? createClient(supabaseUrl as string, supabasePublishableKey as string, {
			auth: { lock: authLock },
		})
	: createMissingConfigClient();
